import {
  AfterViewInit,
  Component,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { BreakpointObserver } from '@angular/cdk/layout';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NGXLogger } from 'ngx-logger';
import { NgPipesModule } from 'ngx-pipes';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { fromEvent, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, tap } from 'rxjs/operators';

import { fuseAnimations } from '../../../../@fuse/animations';
import { FuseDirectivesModule } from '../../../../@fuse/directives/directives';
import { FusePipesModule } from '../../../../@fuse/pipes/pipes.module';
import { FuseTranslationLoaderService } from '../../../../@fuse/services/translation-loader.service';
import { ProductCategoryService } from '../../api/product-category.service';
import { ProductsService } from '../../api/products.service';
import { GT2PageAbstract } from '../../content/abstract/GT2PageAbstract';
import { Gt2PrimengModule } from '../../modules/gt2-primeng.module';
import {
  EditComboPanelData,
  EditComboPanelService,
} from '../../services/edit-combo-panel.service';
import { MobileLayoutService } from '../../services/mobile-layout.service';
import { ModulesService } from '../../services/modules.service';
import { ItemUrlFinder } from '../../utils/item-url-finder';
import { CreateProductComponent } from '../dialogs/create-product/create-product.component';
import { TagsService } from '../tags/tags.service';
import { locale as english } from './i18n/en-CA';
import { locale as french } from './i18n/fr-CA';

const SPINNER = 'comboPanelSpinner';

/**
 * Panneau latéral « Modifier le combo » (Produit).
 *
 * Gauche : produits catalogue (recherche + catégorie + tags) — sans combo, ou
 * sans combo DYNAMIQUE quand le combo édité est lui-même dynamique (un
 * dynamique peut contenir des combos statiques).
 * Droite : contenu du combo — réordonnancement, quantité, retrait.
 *
 * Persistance : attach / detach / move / +/- sont envoyés EN DIRECT ;
 * « Enregistrer » pousse les quantités saisies (POST combo/update). L'appelant
 * recharge dans tous les cas de fermeture.
 *
 * Contexte commande (`orderContext`) : les enfants sont des CLONES commandés —
 * uuid ≠ catalogue, donc jamais de dédoublonnage par uuid entre les listes ;
 * après chaque attach on remplace `combo` par `response.product.childs`.
 */
@Component({
  selector: 'app-edit-combo-panel',
  templateUrl: './edit-combo-panel.component.html',
  styleUrls: ['./edit-combo-panel.component.scss'],
  animations: fuseAnimations,
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    FuseDirectivesModule,
    FusePipesModule,
    Gt2PrimengModule,
    MatButtonModule,
    MatCheckboxModule,
    MatTooltipModule,
    DragDropModule,
    MatInputModule,
    MatIconModule,
    TranslateModule,
    MatProgressSpinnerModule,
    NgPipesModule,
    NgxSpinnerModule,
  ],
})
export class EditComboPanelComponent
  extends GT2PageAbstract
  implements OnInit, AfterViewInit, OnDestroy
{
  product: any;
  products: any[] = [];
  combo: any[] = [];
  comboCopy: any[] = [];
  creationMode: boolean = false;
  orderContext: boolean = false;

  isLoading: boolean = false;
  isEmpty: boolean = true;
  saving: boolean = false;
  /** Une opération enfant (attach / detach / move / qty) est en vol. */
  childProcess: boolean = false;

  @ViewChild('filter') filter?: ElementRef;
  @ViewChild('productList') productList: ElementRef | any;
  filterValue: string = '';
  fromEventSubscription?: Subscription;
  newDialogRef: any;
  isSmallScreen: boolean = false;
  readonly mobileLayout = inject(MobileLayoutService);

  // Filtres additionnels (catégorie de produit + tags) — mêmes sources que le
  // panneau « Ajouter un produit ».
  productCategories: any[] = [];
  selectedCategoryUuid: string | null = null;
  availableTags: any[] = [];
  selectedTagUuids: string[] = [];

  readonly minCharsForEmptyMessage = 3;

  private pendingComboReorderRevert: { from: number; to: number } | null = null;
  private preserveLocalComboOrderAfterMove = false;

  constructor(
    private panelService: EditComboPanelService,
    private translationLoader: FuseTranslationLoaderService,
    public translate: TranslateService,
    public productsService: ProductsService,
    public moduleService: ModulesService,
    private dialog: MatDialog,
    public router: Router,
    private logger: NGXLogger,
    private spinner: NgxSpinnerService,
    private breakpointObserver: BreakpointObserver,
    private tagsService: TagsService,
    private productCategoryService: ProductCategoryService,
  ) {
    super();
    this.translationLoader.loadTranslations(english, french);
    const data: EditComboPanelData | null = this.panelService.getData();
    if (data?.product) {
      this.product = data.product;
    }
    this.combo = Array.isArray(data?.products) ? [...data!.products!] : [];
    this.comboCopy = JSON.parse(JSON.stringify(this.combo));
    this.creationMode = data?.mode === 'create';
    this.orderContext = !!data?.orderContext;
  }

  ngOnInit(): void {
    this.breakpointObserver.observe(['(max-width: 600px)']).subscribe({
      next: (result) => {
        this.isSmallScreen = result.matches;
      },
    });

    this.productCategoryService.getCategoriesList().subscribe({
      next: (response: any) => {
        this.productCategories = response?.data ?? [];
      },
      error: (error: any) => {
        this.logger.error(
          'EditComboPanelComponent.getCategoriesList() -> ERROR: ' +
            JSON.stringify(error),
        );
      },
    });

    const tagFamilies = [
      TagsService.TAG_FAMILY_PRODUCT_OPEN,
      TagsService.TAG_FAMILY_PRODUCT_MOMENT,
      TagsService.TAG_FAMILY_PRODUCT_SEASON,
      TagsService.TAG_FAMILY_PRODUCT_FEATURE,
      TagsService.TAG_FAMILY_PRODUCT_CUSTOM,
    ];
    for (const family of tagFamilies) {
      this.tagsService.getTagsByFamily(family).subscribe({
        next: (response: any) => {
          const tags = response.data || response || [];
          this.availableTags = [
            ...this.availableTags,
            ...tags.filter(
              (t: any) => !this.availableTags.some((at) => at.uuid === t.uuid),
            ),
          ];
        },
      });
    }
  }

  ngAfterViewInit(): void {
    this.fromEventSubscription = fromEvent(this.filter?.nativeElement, 'keyup')
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap(() => {
          if (this.canSearch) {
            this.loadProductList();
          } else {
            this.products = [];
            this.isEmpty = true;
          }
        }),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.fromEventSubscription?.unsubscribe();
  }

  // ---------------------------------------------------------------- recherche

  get hasActiveFilters(): boolean {
    return !!this.selectedCategoryUuid || this.selectedTagUuids.length > 0;
  }

  get canSearch(): boolean {
    return (this.filterValue?.trim().length ?? 0) >= 2 || this.hasActiveFilters;
  }

  get showSearchProductHint(): boolean {
    return (
      this.isEmpty &&
      !this.isLoading &&
      !this.hasActiveFilters &&
      (this.filterValue?.trim().length ?? 0) < this.minCharsForEmptyMessage
    );
  }

  get showNoSearchResults(): boolean {
    return (
      this.isEmpty &&
      !this.isLoading &&
      (this.hasActiveFilters ||
        (this.filterValue?.trim().length ?? 0) >= this.minCharsForEmptyMessage)
    );
  }

  /** Checkbox « multiplier » : module requis + le produit est bien un combo. */
  get showMultiplyCheckbox(): boolean {
    return (
      this.moduleService.modulesAreSet &&
      this.moduleService.hasMultiplicationComboChilds() &&
      !!this.product &&
      (this.product.combo || this.product.is_combo || this.combo.length > 0)
    );
  }

  public onProductFilterChange(): void {
    if (this.productList?.nativeElement) {
      this.productList.nativeElement.scrollTop = 0;
    }
    if (this.canSearch) {
      this.loadProductList();
    } else {
      this.products = [];
      this.isEmpty = true;
    }
  }

  public loadProductList(): void {
    this.isLoading = true;
    this.isEmpty = false;
    // Combo dynamique : ses choix peuvent être des combos statiques → on
    // n'exclut que les dynamiques. Sinon : produits-feuilles seulement.
    const request$ = this.product?.is_choice_product_combo
      ? this.productsService.getProductsListWithoutComboDynamic(
          this.filterValue,
          this.selectedCategoryUuid,
          this.selectedTagUuids,
        )
      : this.productsService.getProductsListWithoutCombo(
          this.filterValue,
          this.selectedCategoryUuid,
          this.selectedTagUuids,
        );
    request$.subscribe({
      next: (response: any) => {
        this.isLoading = false;
        const list: any[] = (response?.data ?? []).filter(
          (p: any) => p.uuid !== this.product?.uuid,
        );
        this.products = list;
        this.isEmpty = list.length === 0;
        if (this.productList?.nativeElement) {
          this.productList.nativeElement.scrollTop = 0;
        }
      },
      error: (error: any) => {
        this.logger.error(
          'EditComboPanelComponent.loadProductList() -> ERROR: ' +
            JSON.stringify(error),
        );
        this.handleAPIError(error, this.dialog, null, null, this.router);
        this.isLoading = false;
        this.isEmpty = true;
      },
    });
  }

  public clearSearch(): void {
    this.filterValue = '';
    if (this.hasActiveFilters) {
      this.loadProductList();
    } else {
      this.products = [];
      this.isEmpty = true;
    }
  }

  public comboSuffix(p: any): string {
    if (!p?.is_combo) {
      return '';
    }
    const key = p.is_choice_product_combo
      ? 'COMBO_PANEL.COMBO_DYNAMIC_SUFFIX'
      : 'COMBO_PANEL.COMBO_SUFFIX';
    return ' | ' + this.translate.instant(key);
  }

  // ------------------------------------------------------------ drag & drop

  public onDrop(event: any): void {
    if (event.previousContainer === event.container) {
      if (event.previousIndex === event.currentIndex) {
        return;
      }
      moveItemInArray(this.combo, event.previousIndex, event.currentIndex);
      this.preserveLocalComboOrderAfterMove = true;
      this.pendingComboReorderRevert = {
        from: event.previousIndex,
        to: event.currentIndex,
      };
      this.onChildDrop(event.item.data, event.currentIndex);
      return;
    }

    if (event.item.data == undefined) {
      return;
    }
    this.attachToCombo(event.item.data, event.currentIndex);
  }

  /**
   * Attache un produit catalogue au combo, puis le place à `targetIndex` si un
   * index de dépôt est fourni. `combo` est remplacé par `response.product.childs`
   * (indispensable en contexte commande : l'enfant est un clone, uuid neuf).
   */
  private attachToCombo(item: any, targetIndex?: number): void {
    this.beginChildProcess();
    const knownUuids = new Set(this.combo.map((c) => c.uuid));
    this.productsService
      .productComboAttach(this.product.uuid, item.uuid, 1)
      .subscribe({
        next: (response: any) => {
          const childs: any[] =
            response?.product?.childs ?? response?.data?.childs ?? [];
          this.combo = childs;
          this.comboCopy = JSON.parse(JSON.stringify(this.combo));
          const newChild =
            (response?.new_product?.uuid
              ? childs.find((c) => c.uuid === response.new_product.uuid)
              : undefined) ??
            childs.find((c) => !knownUuids.has(c.uuid)) ??
            childs[childs.length - 1];
          const lastIndex = childs.length - 1;
          if (
            newChild &&
            targetIndex !== undefined &&
            targetIndex >= 0 &&
            targetIndex < lastIndex
          ) {
            const from = this.combo.indexOf(newChild);
            moveItemInArray(this.combo, from, targetIndex);
            this.preserveLocalComboOrderAfterMove = true;
            this.pendingComboReorderRevert = { from, to: targetIndex };
            this.onChildDrop(newChild, targetIndex);
          } else {
            this.endChildProcess();
          }
        },
        error: (error: any) => {
          this.logger.error(
            'EditComboPanelComponent.attachToCombo() -> ERROR: ' +
              JSON.stringify(error),
          );
          this.endChildProcess();
          this.handleAPIError(error, this.dialog, null, null, this.router);
        },
      });
  }

  private onChildDrop(child: any, movedToIndex?: number): void {
    if (this.combo.length < 2) {
      this.resetReorderState();
      this.endChildProcess();
      return;
    }

    let i = -1;
    if (
      movedToIndex !== undefined &&
      movedToIndex >= 0 &&
      movedToIndex < this.combo.length &&
      this.combo[movedToIndex]?.uuid === child?.uuid
    ) {
      i = movedToIndex;
    } else {
      i = this.combo.findIndex((c) => c.uuid === child?.uuid);
    }
    if (i < 0) {
      this.resetReorderState();
      this.endChildProcess();
      return;
    }

    this.beginChildProcess();
    const request$ =
      i === 0
        ? this.productsService.productChildMoveBefore(
            this.product.uuid,
            child.uuid,
            this.combo[i + 1].uuid,
          )
        : this.productsService.productChildMoveAfter(
            this.product.uuid,
            child.uuid,
            this.combo[i - 1].uuid,
          );

    request$.subscribe({
      next: (response: any) => this.applyMoveApiSuccess(response),
      error: (error: any) => {
        this.logger.error(
          'EditComboPanelComponent.onChildDrop() -> ERROR (move): ' +
            JSON.stringify(error),
        );
        this.applyMoveApiError(error);
      },
    });
  }

  /** Enfants combo d'une réponse move-before / move-after (formats variables). */
  private comboChildsFromMoveResponse(response: any): any[] | null {
    const childs =
      response?.data?.childs ??
      response?.data?.product?.childs ??
      response?.product?.childs;
    return Array.isArray(childs) && childs.length > 0 ? childs : null;
  }

  private applyMoveApiSuccess(response: any): void {
    const dragReorderOnly = this.preserveLocalComboOrderAfterMove;
    this.pendingComboReorderRevert = null;
    this.endChildProcess();
    if (!dragReorderOnly) {
      const childs = this.comboChildsFromMoveResponse(response);
      if (childs && childs.length === this.combo.length) {
        this.combo = childs;
      }
    }
    this.preserveLocalComboOrderAfterMove = false;
    if (!dragReorderOnly) {
      this.comboCopy = JSON.parse(JSON.stringify(this.combo));
    }
  }

  private applyMoveApiError(error: any): void {
    if (this.pendingComboReorderRevert) {
      const { from, to } = this.pendingComboReorderRevert;
      moveItemInArray(this.combo, to, from);
    }
    this.resetReorderState();
    this.endChildProcess();
    this.handleAPIError(error, this.dialog, null, null, this.router);
  }

  private resetReorderState(): void {
    this.pendingComboReorderRevert = null;
    this.preserveLocalComboOrderAfterMove = false;
  }

  // ----------------------------------------------------------- liste droite

  public onAddFromCatalogue(item: any): void {
    const existing = this.orderContext
      ? undefined
      : this.combo.find((c) => c.uuid === item.uuid);
    if (existing) {
      this.onAddItem(existing);
      return;
    }
    this.attachToCombo(item);
  }

  public onAddItem(item: any): void {
    const previous = Number(item.qty) || 0;
    item.qty = previous + 1;
    this.persistChildQty(item, () => {
      item.qty = previous;
    });
  }

  public onLessItem(item: any): void {
    const previous = Number(item.qty) || 0;
    if (previous - 1 <= 0) {
      return;
    }
    item.qty = previous - 1;
    this.persistChildQty(item, () => {
      item.qty = previous;
    });
  }

  public onItemQtyChange(value: any, item: any): void {
    // Mutation en place (identité conservée → pas de perte de focus).
    // Persisté par « Enregistrer ».
    item.qty = value;
  }

  private persistChildQty(item: any, revert: () => void): void {
    const qty = Number(item.qty);
    if (!qty || qty <= 0) {
      revert();
      return;
    }
    this.beginChildProcess();
    this.productsService
      .productComboUpdate(this.product.uuid, item.uuid, qty)
      .subscribe({
        next: () => {
          const copy = this.comboCopy.find((c) => c.uuid === item.uuid);
          if (copy) {
            copy.qty = item.qty;
          }
          this.endChildProcess();
        },
        error: (error: any) => {
          this.logger.error(
            'EditComboPanelComponent.persistChildQty() -> ERROR: ' +
              JSON.stringify(error),
          );
          revert();
          this.endChildProcess();
          this.handleAPIError(error, this.dialog, null, null, this.router);
        },
      });
  }

  public onRemoveItem(item: any): void {
    const index = this.combo.indexOf(item);
    if (index < 0) {
      return;
    }
    this.combo.splice(index, 1);
    this.beginChildProcess();
    this.productsService
      .productComboDetach(this.product.uuid, item.uuid, 0)
      .subscribe({
        next: () => {
          this.comboCopy = this.comboCopy.filter((c) => c.uuid !== item.uuid);
          this.endChildProcess();
        },
        error: (error: any) => {
          this.logger.error(
            'EditComboPanelComponent.onRemoveItem() -> ERROR: ' +
              JSON.stringify(error),
          );
          this.combo.splice(index, 0, item);
          this.endChildProcess();
          this.handleAPIError(error, this.dialog, null, null, this.router);
        },
      });
  }

  /** Flag parent `multiply_combo_item_price` : PATCH du produit, l'appelant recharge à la fermeture. */
  public onProductComboMultiply(): void {
    this.beginChildProcess();
    this.productsService
      .updateProduct(this.product, this.product.uuid)
      .subscribe({
        next: () => {
          this.endChildProcess();
        },
        error: (error: any) => {
          this.product.multiply_combo_item_price =
            !this.product.multiply_combo_item_price;
          this.endChildProcess();
          this.handleAPIError(error, this.dialog, null, null, this.router);
        },
      });
  }

  // ------------------------------------------------------------ sauvegarde

  private combosEqual(): boolean {
    if (this.combo.length !== this.comboCopy.length) {
      return false;
    }
    for (let i = 0; i < this.combo.length; i++) {
      const a = this.combo[i];
      const b = this.comboCopy[i];
      if (a.uuid !== b.uuid || Number(a.qty) !== Number(b.qty)) {
        return false;
      }
    }
    return true;
  }

  public saveData(): void {
    if (this.saving || this.childProcess) {
      return;
    }
    if (this.combo.length === 0 || this.combosEqual()) {
      this.panelService.emitResult(true);
      return;
    }
    const children = this.combo.map((c, i) => ({
      uuid: c.uuid,
      qty: Number(c.qty) || 1,
      order: i,
    }));
    this.saving = true;
    this.productsService
      .productComboQtyUpdate(this.product.uuid, children)
      .subscribe({
        next: () => {
          this.saving = false;
          this.panelService.emitResult(true);
        },
        error: (error: any) => {
          this.saving = false;
          this.logger.error(
            'EditComboPanelComponent.saveData() -> ERROR: ' +
              JSON.stringify(error),
          );
          this.handleAPIError(error, this.dialog, null, null, this.router);
        },
      });
  }

  public close(): void {
    this.panelService.emitResult(false);
  }

  // ---------------------------------------------------------------- divers

  public onCreateProduct(): void {
    this.newDialogRef = this.dialog.open(CreateProductComponent, {
      width: '340px',
      disableClose: false,
      data: { autoRedirect: false },
    });

    this.newDialogRef.afterClosed().subscribe({
      next: (result: any) => {
        if (result) {
          this.attachToCombo(result);
          if (this.canSearch) {
            this.loadProductList();
          }
        }
        this.newDialogRef = null;
      },
    });
  }

  public onOpenInNewTab(item: any): void {
    const prefix = localStorage.getItem('PREFIX') || '';
    window.open(prefix + ItemUrlFinder.getItemURL(item), '_blank');
  }

  private beginChildProcess(): void {
    this.childProcess = true;
    this.spinner.show(SPINNER);
  }

  private endChildProcess(): void {
    this.childProcess = false;
    this.spinner.hide(SPINNER);
  }
}
